import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import type { LabsPlacement } from '../../core/events';
import Logo240 from './Logo240';
import PreviewNotice from './PreviewNotice';

/**
 * The bar at the top of every route.
 *
 * The same bar every 240 Labs tool puts at the top of its pages: the orange mark at the left,
 * then the tool's name; a cluster of controls at the right; one hairline underneath; and the
 * full width of the window rather than the page's column — so moving between tools does not
 * read as leaving. `packages/one-step-ahead/src/frontend/App.tsx` and
 * `packages/objectives/src/components/ObjectivesHeader.tsx` are the same bar. The mark and
 * the Labs chip go to Labs; the name and "Your groups" go to this teacher's groups, which is
 * Collab's home and the way back from anywhere inside it.
 *
 * It is rendered *outside* the per-route screens so that it survives navigation — a header
 * each screen drew for itself is a header that disappears the moment a screen forgets to draw
 * it, which is how the account control went missing from everything except the one screen
 * that passed it in.
 *
 * `action` is a slot rather than an import. Clerk's `UserButton` throws outside a
 * `ClerkProvider`, and this package has two builds that have no provider at all — the
 * test-identity bundle and a bundle built without a publishable key. Whichever wrapper has a
 * provider passes the control in; the others pass what they have, and the header still
 * renders.
 */

export const LABS_HOME = 'https://labs.240.org';

export interface ShellHeaderProps {
  /** The account control: Clerk's `UserButton`, the test identity's address, or nothing. */
  action?: ReactNode;
  /** Told when a teacher takes the bar back to Labs. Absent where nothing is tracked. */
  onLabsClicked?: (placement: LabsPlacement) => void;
}

export default function ShellHeader({ action, onLabsClicked }: ShellHeaderProps) {
  return (
    <>
      <header className="preview-nav">
        <div className="preview-nav-brand">
          <a className="preview-nav-logo" href={LABS_HOME} title="240 Labs" aria-label="240 Labs home"
            onClick={() => onLabsClicked?.('logo')}>
            <Logo240 className="preview-nav-mark shell-logo" aria-hidden="true" focusable="false" />
          </a>
          <Link className="preview-nav-name" to="/" aria-label="240 Collab">Collab</Link>
        </div>
        <div className="preview-nav-cluster">
          <nav className="shell-nav" aria-label="Main navigation">
            <NavLink to="/" end>Your groups</NavLink>
          </nav>
          <a className="preview-labs" href={LABS_HOME} aria-label="240 Labs" onClick={() => onLabsClicked?.('navigation')}>
            <svg className="preview-labs-icon" aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
            </svg>
            <span className="preview-labs-word">Labs</span>
          </a>
          {/*
            * `ph-no-capture` blocks this corner from session replay. It holds a name, an address
            * and an avatar — the teacher's identity, which is not what a recording of the product
            * is for.
            */}
          <div className="shell-actions ph-no-capture">{action}</div>
        </div>
      </header>
      <PreviewNotice />
    </>
  );
}
